import React from "react";
import { Timeline } from "primereact/timeline";
import { Message } from "primereact/message";

import { ActorRole, JourneyStep, JourneyStepItem } from "../../../../types/onboarding";
import { actorLabel } from "../../../../utils/actors";

interface JourneyProgressProps {
  steps: JourneyStep[];
  // Null when nothing is owed: a finished or an ended onboarding.
  nextAction: string | null;
  ended?: boolean;
}

const formatDate = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleString();
};

const roleLabel = (role: ActorRole): string => {
  switch (role) {
    case "OPS":
      return "Ops";
    case "CUSTOMER":
      return "Customer";
    case "SYSTEM":
      return "System";
    default:
      return role;
  }
};

// "Ops", or "Customer (DE-COC-…)" where the subject says more than the role.
// The raw subject is an opaque id for ops and an eMAID for the customer, so it
// is only worth adding when it differs from the role name.
const whoDidIt = (
  actor: string | null,
  role: ActorRole | null
): string | null => {
  if (!role) return null;
  const name = roleLabel(role);
  const subject = actorLabel(actor);
  return subject && subject !== name ? `${name} (${subject})` : name;
};

// Resign reasons come back as codes (e.g. TOO_EXPENSIVE); never show the raw one.
const reasonLabel = (code: string): string =>
  code
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());

// Colours come from the theme's CSS variables, not PrimeFlex `bg-*` utilities:
// this app does not load PrimeFlex's background layer, so `bg-primary` and
// friends compute to transparent and the markers render invisible.
const MARKER: Record<
  JourneyStep["status"],
  { icon: string; background: string; color: string }
> = {
  done: {
    icon: "pi pi-check",
    background: "var(--green-500, #22c55e)",
    color: "var(--primary-color-text, #ffffff)",
  },
  // Passed but never recorded — must not read as a completed step.
  skipped: {
    icon: "pi pi-minus",
    background: "var(--surface-300, #d1d5db)",
    color: "var(--text-color-secondary, #6b7280)",
  },
  current: {
    icon: "pi pi-circle-fill",
    background: "var(--primary-color, #10b981)",
    color: "var(--primary-color-text, #ffffff)",
  },
  pending: {
    icon: "pi pi-circle",
    background: "var(--surface-300, #d1d5db)",
    color: "var(--text-color-secondary, #6b7280)",
  },
  "not-reached": {
    icon: "pi pi-minus",
    background: "var(--surface-200, #e5e7eb)",
    color: "var(--text-color-secondary, #6b7280)",
  },
  failed: {
    icon: "pi pi-times",
    background: "var(--red-500, #ef4444)",
    color: "var(--primary-color-text, #ffffff)",
  },
};

/**
 * The prospect's whole onboarding journey (CWA-94): what is done, where it is
 * now, and what is still outstanding.
 *
 * Distinct from the status history below it, which lists only what already
 * happened. This view exists so an agent can tell what a prospect needs next
 * without knowing the pipeline by heart.
 */
const JourneyProgress: React.FC<JourneyProgressProps> = ({
  steps,
  nextAction,
  ended = false,
}) => {
  if (!steps.length) {
    return <p className="text-500">No progress to show yet.</p>;
  }

  // The step the prospect is on: the current one, or, where a part of a step
  // failed and nothing is current, that step. The next action is said there.
  const current = steps.find((s) => s.status === "current");
  const now = current ?? steps.find((s) => s.status === "failed");

  // A part of a step, as a line under it: the step's marker icon, small, in
  // the marker's colour where the part is decided and greyed where it is not.
  const item = (part: JourneyStepItem, i: number) => {
    const open = part.status === "pending" || part.status === "skipped";
    const { icon, background, color } = MARKER[part.status];
    return (
      <li key={i} className="flex gap-2 align-items-start mt-1">
        <i
          className={icon}
          style={{
            fontSize: "0.7rem",
            marginTop: "0.3rem",
            color: open ? color : background,
          }}
        />
        <div>
          <div className={open ? "text-500" : undefined}>{part.label}</div>
          {(part.at || part.actor_role) && (
            <div className="text-500 text-xs">
              {[formatDate(part.at), whoDidIt(part.actor, part.actor_role)]
                .filter(Boolean)
                .join(" · ")}
            </div>
          )}
          {part.note && <div className="text-xs">{part.note}</div>}
        </div>
      </li>
    );
  };

  const marker = (step: JourneyStep) => {
    const { icon, background, color } = MARKER[step.status];
    return (
      <span
        className="flex align-items-center justify-content-center border-circle"
        style={{ width: "1.75rem", height: "1.75rem", background, color }}
      >
        <i className={icon} style={{ fontSize: "0.75rem" }} />
      </span>
    );
  };

  const content = (step: JourneyStep) => (
    <div className="mb-3">
      <div
        className={
          step.status === "current"
            ? "font-bold"
            : step.status === "done" || step.status === "failed"
            ? "font-medium"
            : "text-500"
        }
      >
        {step.label}
      </div>
      {step.status === "skipped" && !step.items?.length && (
        <div className="text-500 text-sm">Not recorded</div>
      )}
      {(step.completed_at || step.actor_role) && (
        <div className="text-500 text-sm">
          {[formatDate(step.completed_at), whoDidIt(step.actor, step.actor_role)]
            .filter(Boolean)
            .join(" · ")}
        </div>
      )}
      {step.note && <div className="text-sm mt-1">{step.note}</div>}
      {!!step.items?.length && (
        <ul className="list-none p-0 m-0 text-sm" data-testid="journey-step-items">
          {step.items.map(item)}
        </ul>
      )}
      {(step.resign_reason || step.resign_note) && (
        <div className="text-sm mt-1">
          {step.resign_reason && (
            <div>Reason: {reasonLabel(step.resign_reason)}</div>
          )}
          {step.resign_note && <div>Note: {step.resign_note}</div>}
        </div>
      )}
      {step === now && nextAction && (
        <div className="text-sm mt-1 font-medium">Next: {nextAction}</div>
      )}
    </div>
  );

  return (
    <div data-testid="journey-progress">
      {ended && (
        <Message
          severity="warn"
          className="w-full mb-3"
          text="This onboarding was ended — the remaining steps were never reached."
        />
      )}
      {/* The Timeline reserves an "opposite" column even when nothing fills it,
          which pushed every step into the right half of the page. Collapsing it
          keeps the journey against the left edge. */}
      <Timeline
        value={steps}
        align="left"
        marker={marker}
        content={content}
        pt={{ opposite: { style: { flex: 0, padding: 0 } } }}
      />
    </div>
  );
};

export default JourneyProgress;
